import React from 'react';
import BlogArticle from 'components/blog/blogArticle';
import { Post } from 'components/blog/content/styled';
import { authorAaron as author } from 'data/authors';
import Link from 'components/ui/link';
import img from 'img/blog/covers/8_cro_tips.png';
import img1 from 'img/blog/get-more-leads-this-year/website.png';
import img2 from 'img/blog/get-more-leads-this-year/accounting.png';
import img3 from 'img/blog/get-more-leads-this-year/oil.png';
import img4 from 'img/blog/get-more-leads-this-year/courses.png';
import img5 from 'img/blog/get-more-leads-this-year/appstore.png';
import img6 from 'img/blog/get-more-leads-this-year/netflix.png';
import img7 from 'img/blog/get-more-leads-this-year/ls.png';
import img8 from 'img/blog/get-more-leads-this-year/timeline.png';
import img9 from 'img/blog/get-more-leads-this-year/app.png';
import img10 from 'img/blog/get-more-leads-this-year/desktops.png';
import img11 from 'img/blog/get-more-leads-this-year/babbel.png';
import img12 from 'img/blog/get-more-leads-this-year/contact.png';
import img13 from 'img/blog/get-more-leads-this-year/fb.png';
import img14 from 'img/blog/get-more-leads-this-year/mothersday.png';

const Content = () => {
  return (
    <Post>
      <p>
        Lead generation, or creating interest in your brand, is an important step towards increasing
        your profit. But, if you can’t convert that interest into actual customers, all that effort
        is lost.{' '}
      </p>

      <p>
        That’s why a <Link href="/blog/low-conversion-rate/">low conversion rate</Link> can be so
        damaging. You don’t just want views on your site — you want active visitors who interact
        with your content and invest in your products and services.
      </p>

      <p>
        The way to achieve this is through conversion rate optimization. CRO is about more than just
        having the best landing page possible. It’s about cultivating a website experience that
        supports potential customers along every stage of their journey, from discovery to purchase,
        to follow-up and beyond.{' '}
      </p>

      <p>
        That’s a tall order, so we’re going to break it down into eight steps you can take to
        improve your CRO today.{' '}
      </p>
      <h2>Clearly define your ideal customers</h2>
      <p>
        The first step to targeting the right customers is identifying who they are. Creating buyer
        personas can help you to decide everything, from what social media platforms you need to be
        using, to which kinds of content you should be publishing.{' '}
      </p>

      <p>
        To do this effectively, you have to look at your business from a few different parameters.{' '}
      </p>

      <p>
        First, consider your industry as a whole. Who usually benefits from your offerings, and who
        else might be interested?{' '}
      </p>

      <p>
        Then, look at your competitors. Who do they target? Why? Most importantly: how does your
        business differ from others in the industry? The things that set you apart can help decide
        which market segment you can target most effectively. This could be influenced by which
        groups you have unique access to, or by what your products or services can provide that the
        rest of the industry doesn’t.{' '}
      </p>

      <p>
        Finally, look at your products or services individually. You might have a variety of
        different offerings that each fit best with a slightly different market segment. Each
        product, service, and physical location (if applicable) should have its own webpage
        dedicated to just that segment and just the customers it targets.{' '}
      </p>
      <h2>Make it as easy as possible for prospective customers to get in touch</h2>
      <p>
        Once you have an idea of what your ideal customers look like, you can begin targeting them
        more effectively. Make sure that you have a presence on all the social media sites they are
        likely to frequent —{' '}
        <Link
          href="https://www.socialpilot.co/social-media-analytics-tools"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          social media analytics tools
        </Link>{' '}
        can help you to work out where you should be focusing your efforts. And look for other ways
        to ensure it’s as easy as possible for prospective customers to get in touch.{' '}
      </p>

      <p>
        Different people like to communicate in different ways, so you should offer a number of
        contact options. These may include but are not limited to phone, email, social media, and
        instant messaging, usually in the form of a chatbot.{' '}
      </p>

      <p>
        <Link href="/blog/the-importance-of-customer-service/">
          The importance of online customer service
        </Link>{' '}
        is even more obvious now than ever before. When the majority of customer interactions are
        happening online, having the best customer service is not just necessary, it’s practically
        the bare minimum.{' '}
      </p>

      <p>
        To ensure that you satisfy this criterion, keep all lines of communication open. Stay active
        on social media, emails, and other methods of contact that you make available to visitors.{' '}
      </p>

      <p>
        Utilize consumer feedback from buyer-satisfaction surveys, comment sections, and general
        feedback forms, too. These types of communication are helpful to gauge how to better serve
        your customers.{' '}
      </p>
      <p>
        Listening to your customer base is the best way to make sure that you are truly catering to
        their needs in the ways that they have come to expect.{' '}
      </p>
      <h2>Build trust with authoritative content that shows your expertise</h2>
      <p>
        Publishing expert content on your website will help you earn your customers’ trust and can
        also help to boost your SEO.{' '}
      </p>

      <p>
        There are a lot of different types of content that will help to demonstrate your expertise.
        Combining the strengths of your business with the particular needs of your target customers
        will ensure that the content you create is uniquely designed to demonstrate your expertise,
        authority, and trustworthiness.
      </p>

      <p>
        Some of the content types that you could create to build your authority include the
        following:{' '}
      </p>
      <ul>
        <li>Comprehensive guides on topics relevant to your niche</li>
        <li>Video tutorials for how to use your product or what to expect from your service </li>
        <li>Webinars educating visitors about topics you’re an expert in</li>
        <li>Blogs with original content and guest posts from other authorities in your niche </li>
        <li>Presentations of original research </li>
      </ul>
      <p>
        It might be difficult to imagine how you can make these types of content work for your
        website if you’ve never created them before. To give you some inspiration, we’ve collected
        some real-world examples of companies that are already successfully demonstrating their
        expertise, building their authority, and earning the trust of their ideal customers with
        content.{' '}
      </p>
      <img
        src={img1}
        alt="Canon View Dental"
        title="Build trust with authoritative content that shows your expertise"
      />
      <p>
        {' '}
        Roadside Dental Marketing specializes in helping dentists to promote their services and
        attract the clients they need. And, to show that they’re experts in this area, they put
        together a round-up of{' '}
        <Link
          href="https://www.roadsidedentalmarketing.com/blog/best-dental-websites/"
          rel="noopener noreferrer nofollow"
          target="_blank"
        >
          the best dental websites of 2020
        </Link>
        . This celebrates their own clients, while also demonstrating their past work, knowledge of
        the field, and the results they’ve been able to get for the companies they’ve helped.
      </p>
      <img src={img2} alt="accounting" title="What is Cloud Accounting Software?" />
      <p>
        FreshBooks, an accounting software provider, has a gallery of articles that cover a variety
        of topics that are of interest to their target customers — small business owners or
        freelancers.{' '}
      </p>

      <p>
        They cover accounting, marketing, productivity, invoicing, and more. Their{' '}
        <Link
          href="https://www.freshbooks.com/hub/accounting/cloud-accounting-software"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          guide to cloud accounting software
        </Link>{' '}
        is particularly helpful for positioning the company as an authority in its field. Not only
        does it show that the business knows its industry and understand the needs of its clients,
        but it also helps readers to familiarize themselves with FreshBooks’s product offerings,
        which is great for generating leads.
      </p>
      <img src={img3} alt="example of lead" title="What is CBD Oil? Our Ultimate Guide" />
      <p>
        Similarly, Joy Organics, a vendor of CBD oil and CBD-infused products, created an{' '}
        <Link
          href="https://joyorganics.com/ultimate-guide-to-cbd-oil/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          ultimate guide to CBD oil
        </Link>{' '}
        to familiarize readers with their products and the benefits associated with them. This guide
        makes it very clear that the company is an expert in its field, which will build trust with
        customers, and give them more of a reason to shop the company’s products.{' '}
      </p>
      <img src={img4} alt="example of lead" title="Courses" />
      <p>
        Finally, Canva, a graphic design platform, offers a series of video{' '}
        <Link
          href="https://designschool.canva.com/courses/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          courses
        </Link>{' '}
        that walk their ideal customers through a whole host of topics they might be interested in,
        such as personal branding, social media marketing, and the basics of design.
      </p>

      <p>
        Again, providing all of this information for free helps to position Canva as an expert and
        authority in the graphic design space. Plus, helping to educate their target customers
        builds a level of trust that can help to encourage a lot more conversions.{' '}
      </p>

      <p>
        Another way to improve your content is to adjust or repurpose what you already have on your
        website. You can{' '}
        <Link href="/blog/how-to-bring-old-content-back-to-life-and-discover-new-levels-of-engagement/">
          boost engagement by giving old content new life
        </Link>{' '}
        through leveraging the power of analytics, reformatting old content (by, for example,
        turning an article into a video), and connecting evergreen posts with new audiences by
        plugging them on different platforms.{' '}
      </p>
      <h2>Only target the most relevant keywords on each page</h2>
      <p>
        Another important aspect for increasing conversions is making sure that your webpages are
        seen by the right people. SEO can help with that.{' '}
      </p>

      <p>
        Through search engine optimization, you can improve your rankings on search engine results
        pages or SERPs. To do this, you have to choose the keywords or search terms that you want to
        rank for.{' '}
      </p>

      <p>
        Using a professional keyword research tool, like{' '}
        <Link
          href="https://ads.google.com/home/tools/keyword-planner/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Google’s Keyword Planner
        </Link>{' '}
        or{' '}
        <Link
          href="https://ahrefs.com/keyword-generator"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Ahrefs’ Keyword Generator
        </Link>
        , can help with this. Search topics related to your niche, and see what keywords are
        suggested.{' '}
      </p>

      <p>
        Also, pay attention to{' '}
        <Link
          href="https://loganix.com/buyer-intent-keywords/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          keywords that signal buyer intent
        </Link>
        . These will be terms that speak to the final stage of the buyer journey when potential
        customers have decided that they need your products or services.{' '}
      </p>

      <p>
        Once you select the right keywords to target, make sure to incorporate them naturally into
        your content.{' '}
      </p>

      <p>
        When you’re looking to climb the SERPs, it’s also worth noting that there are other{' '}
        <Link href="/blog/optimize-your-website-like-a-pro/">
          ways professional web developers optimize their websites
        </Link>
        . These include improving the user experience and making sure that the design of your pages
        is intuitive, simple to follow, and puts all the right content exactly where you need it.{' '}
      </p>
      <h2>Allow your customers to try before they buy whenever possible</h2>
      <p>
        One of the best ways to convince potential customers that they will be satisfied with your
        product or service is by letting them try it before they buy it.{' '}
      </p>

      <p>
        While you don’t want to give away all of your business for free, there are quite a few ways
        to let visitors test-drive your offerings. Here are a few ideas.{' '}
      </p>
      <img
        src={img5}
        alt="example of lead - app store"
        title="Allow your customers to try before they buy whenever possible"
      />
      <p>
        For clothing, accessories, and other designer products, a virtual try-on feature is perfect.
        Glasses vendors with an e-commerce option have been using this feature for ages, and it
        works wonders. Warby Parker has even created an{' '}
        <Link
          href="https://www.warbyparker.com/app"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          app
        </Link>{' '}
        with the express purpose of providing its customers with the chance to see whether their
        glasses suit them before making a purchase.{' '}
      </p>
      <img src={img6} alt="example of lead - Netlfix" title="Get one month free - Netflix" />
      <p>
        {' '}
        If you provide a service of some kind, consider offering a free trial. This will allow
        prospective customers to get a taste of what you do before making a financial commitment.{' '}
      </p>

      <p>
        Streaming services have been using this system for years. For example, Hulu’s{' '}
        <Link href="https://www.hulu.com/start" target="_blank" rel="noopener noreferrer nofollow">
          free trial
        </Link>{' '}
        lasts one month, after which participants become subscribers who are charged monthly for
        continued use of the service. Anyone who has enjoyed the service, or who hasn’t caught up on
        the last season of their favorite show by the time their trial expires, is much more likely
        to consider paying for the next month.{' '}
      </p>
      <img
        src={img7}
        alt="example of lead - LiveSession"
        title="Request a LiveSession demo today!"
      />
      <p>
        If you can’t offer a full free trial, you can simulate the experience of your product or
        service with a demo. This lets potential customers learn exactly what to expect from your
        offerings before they commit, and gives them more confidence in making the leap. For
        instance, LiveSession encourages their target audience to{' '}
        <Link href="/demo/">book a product demo</Link> in order to be better familiarized with their
        analytics tools and helpful features.{' '}
      </p>
      <img
        src={img8}
        alt="example of lead - Timeline Infographic Templates"
        title="Timeline Infographic Templates"
      />
      <p>
        {' '}
        Finally, if you don’t want to give away part of your business’s offerings outright, you can
        create a free tool with the express purpose of solving a related consumer need.{' '}
      </p>

      <p>
        Venngage, a graphic design service that specializes in designing infographics, provides a
        variety of templates that can be used free of charge. While premium and business
        infographics require a subscription, more basic options, like some of their{' '}
        <Link
          href="https://venngage.com/templates/infographics/timeline"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          timeline infographic templates
        </Link>
        , don’t come with a cost.
      </p>
      <h2>Create content for every stage of the buying journey </h2>
      <p>
        Now, we’re going to look more in-depth at what the buyer journey looks like, and how to
        target potential customers across all stages of the process.{' '}
      </p>

      <p>
        Because people are likely to go looking for your products or services while armed with
        different levels of understanding, you want to have content that targets each of these
        stages. A great way to ensure that you target every step of the buyer journey is to{' '}
        <Link href="/user-experience/user-journey-map/">develop a user journey map</Link>. Using
        that as a planning guide, you can design your content around it to successfully target every
        potential consumer.{' '}
      </p>

      <p>
        Here are some examples of how to create content that fits a specific stage of the buyer
        journey.
      </p>

      <p>
        First is the general information-gathering stage. These people have likely identified a
        problem they have that needs solving but don’t yet know what product or service could be the
        solution. Content that targets this stage is considered top of the funnel, and the people
        who encounter it are likely those who don’t know anything about your business or industry
        yet.{' '}
      </p>
      <img
        src={img9}
        alt="Customer Dashboard in SPP"
        title="Create content for every stage of the buying journey"
      />
      <p>
        Service Provider Pro, which offers business client portal software, targets the top of the
        funnel with an explanation of{' '}
        <Link
          href="https://spp.co/blog/client-portal/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          why agencies should invest in a client portal
        </Link>{' '}
        in the first place. This lets them show off their platform and explain how and it’s useful,
        while also educating potential customers about the core purpose of the product.
      </p>

      <p>
        If someone learns about client portals through this guide, they’ll be far more likely to
        circle back and pay for SPP’s services. So, creating this content is a great move for
        attracting your ideal customers at the beginning of their buyer journey, and building a
        level of trust so they eventually pay for your services.{' '}
      </p>

      <p>
        Once people get a better idea of the solution they might need and the options at their
        disposal, they’re probably looking for guides that demonstrate the differences between the
        options, and what works best when.{' '}
      </p>
      <img src={img10} alt="Buing Guide" title="Desktop Buying Guide" />
      <p>
        {' '}
        To satisfy this need, Best Buy, a chain store that sells a variety of tech products, created
        a{' '}
        <Link
          href="https://www.bestbuy.com/site/buying-guides/desktops-buying-guide/pcmcat310900050008.c?id=pcmcat310900050008"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          desktop buying guide
        </Link>{' '}
        to help potential customers narrow down which computer they might be interested in. This
        works particularly well for the company, given that they sell products from a variety of
        different brands, so visitors can easily purchase any of their options right from the same
        website.
      </p>

      <p>
        Then, once visitors figure out which specific product type or service model will be most
        beneficial to them, they will likely have narrowed their search down to two or three
        options. At this point, direct comparisons between top competitors help them to make a final
        choice. For comparison pieces, you generally want to include only a handful of options
        (including at least one of your own) and make the differences as clear as possible. Charts
        are especially helpful.{' '}
      </p>

      <p>
        Babbel language learning software uses a{' '}
        <Link
          href="https://www.babbelforbusiness.com/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          comparison chart
        </Link>{' '}
        of their B2B services to help visitors in the final stage of the buyer’s journey decide on
        the product that they need.{' '}
      </p>
      <img src={img11} alt="Babbel pricing" title="Comparison chart" />
      <p>
        Once a customer has all of the information they require to make the best decision to suit
        their needs, they’ll hopefully decide to buy from you. At this point, your landing pages do
        the rest of the work to capture those sales and lead them directly to the products or
        services they require.{' '}
      </p>
      <h2>Ensure your website provides a positive UX</h2>
      <p>
        If your website is hard to understand, doesn’t work the way it should, or takes too long to
        get things done, you’ll miss out on conversions. But, these problems can be fixed by
        improving your UX.{' '}
      </p>

      <p>
        <Link href="/user-experience/">Optimizing your UX</Link> involves paying attention to a
        variety of features that impact the usability of your website. Here are a few examples of
        problems you might run into and what you can do to solve them.
      </p>
      <ol>
        <li>
          <p>
            <strong>Page load speed</strong>: You want your webpages to load quickly. These days,{' '}
            <Link
              href="https://www.thinkwithgoogle.com/marketing-strategies/app-and-mobile/mobile-page-speed-new-industry-benchmarks/"
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              Google’s
            </Link>{' '}
            best practice recommendation is that you keep your web page load speed under 3 seconds.
            That’s really fast. So how do you make it happen?{' '}
          </p>
          <p>
            For one, you want to make sure that you’ve compressed any images that appear on your
            website. Then, you also want to enable conditional loading, so that anything above the
            fold shows up first, buying time for the rest of the page to load more slowly.{' '}
          </p>
        </li>
        <li>
          <strong>Responsive design</strong>: You want a website that will be well-rendered on a
          variety of different devices. This is known as responsive formatting although, these days,
          it’s becoming even more necessary to format a website for mobile users first. According to{' '}
          <Link
            href="https://www.statista.com/statistics/277125/share-of-website-traffic-coming-from-mobile-devices/"
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            Statista
          </Link>
          , mobile searches have accounted for a small majority of total searches, hovering just
          under 51% in the most recent period (quarter 3 of 2020). This means that, if all else
          fails, you should make sure that your website will load well on mobile devices.{' '}
        </li>
        <li>
          <strong>Readability</strong>: You want your site and your content to be readable. But,
          more than that, you want it to use language that is relatable to your target audience.
          Give them all the information they need, none that they don’t, and break down the
          structure so they don’t have to read through large blocks of text to get to the point.{' '}
        </li>
        <li>
          <strong>Accessibility</strong>: You want your content to be accessible to as wide an
          audience as possible. A few steps that you can take to improve your accessibility are to
          add alt text to images, caption videos, and make sure that any important information can
          be identified using more than one modality. For example, if you use color to signal
          importance, make sure that someone who cannot rely on color to make a distinction between
          information can still use your site properly.{' '}
        </li>
        <li>
          <strong>Ease of navigation</strong>: You also want your layout to be intuitive. Any page
          should be accessible with two clicks from any other page. If people have to go hunting for
          the information they want, they will simply leave your site and go to one that’s less
          confusing. For this step, it can be helpful to create a site map before designing your
          navigation in order to make sure that everything is connected.{' '}
        </li>
      </ol>
      <h2>Make sure your calls-to-action are strong</h2>
      <p>
        When you’re creating your landing pages, you need to ensure each one includes a strong
        call-to-action or CTA. This is the feature that tells visitors what they should do once
        they’re on your landing page. It might be a newsletter sign-up button, a link to your online
        store, or even a way to easily register for your service.{' '}
      </p>

      <p>
        If potential customers have found your landing page, chances are they’re at least somewhat
        interested in your product or service. A strong CTA can give them that little nudge to give
        your business a chance.{' '}
      </p>

      <p>Here are a few tips for creating strong CTAs. </p>

      <p>
        First, your CTA should stand out. It doesn&apos;t have to be the only thing on the page (and
        probably shouldn’t be), but you definitely don’t want visitors to have trouble finding it in
        the first place.{' '}
      </p>
      <img src={img12} alt="contact us" title="Make sure your calls-to-action are strong" />
      <p>
        Huemor, a web design and development company, has large, playful CTAs easily visible on
        their{' '}
        <Link href="https://huemor.rocks/grow/" target="_blank" rel="noopener noreferrer nofollow">
          landing pages
        </Link>
        . Plus, the astronaut puppy is definitely a strong convincer and a good reflection of their
        creative and fun brand image.{' '}
      </p>
      <img src={img13} alt="webinar" title="Facebook webinar" />

      <p>
        Second, you could rely on the fear of missing out to convince people to join you. People
        naturally seek connection, so FOMO can be a powerful motivator. For example,{' '}
        <Link href="https://www.wordstream.com/" target="_blank" rel="noopener noreferrer nofollow">
          Wordstream
        </Link>
        , an online advertising company, uses the fear of missing out to convince website visitors
        to sign up for their Facebook webinar.
      </p>

      <p>
        By using the CTA “save my seat”, they create a feeling of scarcity, which will make users
        want to sign up right now so they don’t miss out on the event. There’s a chance there’s no
        limit on how many people can attend the webinar, but this button suggests otherwise, which
        is very effective because nobody will want to miss out.{' '}
      </p>

      <p>
        Third, you could create a sense of urgency around your offer. “Act Now!” CTAs do a great job
        of this by implying that the best time to take advantage of an offer is right away. Some
        businesses also opt for an on-page countdown, which instinctively communicates that time may
        be running out.{' '}
      </p>
      <img src={img14} alt="gifts" title="Example - Mother's day gifts" />
      <p>
        British luxury chocolatier{' '}
        <Link
          href="https://www.hotelchocolat.com/uk"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          Hotel Chocolat
        </Link>{' '}
        uses upcoming holiday reminders to convey urgency by reminding visitors that they should
        order soon to get their gifts delivered in time for upcoming holidays. If you sell physical
        products, or there are often ordering deadlines your customers need to abide by, shout about
        it! This could well lead to a lot more conversions, as people won’t want to miss their
        chance.{' '}
      </p>

      <p>
        Sometimes all visitors need is a little bit of a push in the right direction. Because of
        this, a strong CTA is one of the best ways to{' '}
        <Link
          href="https://livesession.io/blog/5-easy-ways-to-increase-landing-page/"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          increase your landing page conversion rate
        </Link>
        .
      </p>
      <h2>Summary</h2>
      <p>
        If you want to increase your sales in 2021, follow our 8 steps for optimizing your
        conversion rate.{' '}
      </p>

      <p>
        First, identify your ideal customers, and make sure that potential and current customers can
        easily contact you to address questions or comments. Build your website’s expertise,
        authority, and trust metrics with quality content, and target the right keywords to
        effectively rank that content on the SERPs.{' '}
      </p>

      <p>
        If you can, let customers try your product or service before they buy. This lets them
        develop an affinity for your organization before they have even become an official customer.{' '}
      </p>

      <p>
        Prioritize user experience, from your technical UX all the way to content decisions, like
        creating content tailored to every stage of the buyer journey.{' '}
      </p>

      <p>
        Finally, include a strong CTA on all landing pages, reminding potential customers what they
        should do while they’re visiting your website.
      </p>

      <p>
        For more{' '}
        <Link href="/blog/top-6-tools-for-improving-conversion-in-saas/">
          tools to improve conversion in SaaS organizations
        </Link>
        , check out LiveSession’s helpful roundup.{' '}
      </p>
    </Post>
  );
};

const Wrapped = BlogArticle(Content);

export const frontmatter = {
  title: '8 CRO Tips That Will Help You Get More Leads This Year ',
  url: '/blog/get-more-leads-this-year/',
  description: `You drive high volume traffic to your website but get low conversion rates? Learn how to get more leads with these simple tricks.`,
  author,
  img,
  imgSocial: img,
  date: '2021-05-27',
  category: '',
  group: 'Research',
  timeToRead: 16,
};

export default () => (
  <Wrapped
    title={frontmatter.title}
    category={frontmatter.category}
    description={frontmatter.description}
    date={frontmatter.date}
    author={frontmatter.author}
    url={frontmatter.url}
    img={frontmatter.img}
    imgSocial={process.env.GATSBY_HOST + frontmatter.img}
    group={frontmatter.group}
    timeToRead={frontmatter.timeToRead}
  />
);
